/**
 * Presite Parts Service Layer
 * Handles all Firestore operations for the presite_parts collection
 * Parts selected during presite jobs for quoting and provisioning
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  type DocumentReference,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  PresitePartsSelection,
  ProvisioningItemStatus,
} from '../types/theo-workflow.types';
import { createProvisioningItem } from './provisioning.service';
import { getTenantIdSync, tenantIdReadUnion } from '../hooks/useTenantId';

const COLLECTION_NAME = 'presite_parts';

// ============================================================================
// CREATE
// ============================================================================

/**
 * Create a new presite parts selection
 */
export async function createPresitePart(
  data: Omit<PresitePartsSelection, 'id' | 'createdAt' | 'updatedAt'>,
  userId?: string
): Promise<string> {
  const partData: Record<string, unknown> = {
    dealId: data.dealId,
    tenantId: (data as { tenantId?: string }).tenantId ?? getTenantIdSync(),
    ...(data.partId && { partId: data.partId }),
    partName: data.partName,
    ...(data.sku && { sku: data.sku }),
    quantity: data.quantity,
    ...(data.notes && { notes: data.notes }),
    isCustomPart: data.isCustomPart !== undefined ? data.isCustomPart : !data.partId,
    ...(data.estimatedPrice !== undefined && { estimatedPrice: data.estimatedPrice }),
    addedToPlayground: data.addedToPlayground || false,
    addedToProvisioning: data.addedToProvisioning || false,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    ...(userId && { createdBy: userId }),
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), partData);
  return docRef.id;
}

/**
 * Create multiple presite parts in a batch
 */
export async function createPresitePartsBatch(
  parts: Omit<PresitePartsSelection, 'id' | 'createdAt' | 'updatedAt'>[],
  userId?: string
): Promise<string[]> {
  const batch = writeBatch(db);
  const partRefs: DocumentReference[] = [];

  parts.forEach((part) => {
    const partRef = doc(collection(db, COLLECTION_NAME));
    partRefs.push(partRef);

    const partData: Record<string, unknown> = {
      dealId: part.dealId,
      // REQUIRED by the rules lock — presite_parts creates must carry the caller's tenant.
      tenantId: getTenantIdSync(),
      ...(part.partId && { partId: part.partId }),
      partName: part.partName,
      ...(part.sku && { sku: part.sku }),
      quantity: part.quantity,
      ...(part.notes && { notes: part.notes }),
      isCustomPart: part.isCustomPart !== undefined ? part.isCustomPart : !part.partId,
      ...(part.estimatedPrice !== undefined && { estimatedPrice: part.estimatedPrice }),
      addedToPlayground: part.addedToPlayground || false,
      addedToProvisioning: part.addedToProvisioning || false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      ...(userId && { createdBy: userId }),
    };

    batch.set(partRef, partData);
  });

  await batch.commit();
  const partIds = partRefs.map((ref) => ref.id);

  console.log(`[PRESITE PARTS] Created ${parts.length} parts in batch`);
  return partIds;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Get a single presite part by ID
 */
export async function getPresitePart(
  partId: string
): Promise<PresitePartsSelection | null> {
  const docRef = doc(db, COLLECTION_NAME, partId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as PresitePartsSelection;
}

/**
 * Get all presite parts for a specific deal
 */
export async function getPresitePartsByDeal(
  dealId: string
): Promise<PresitePartsSelection[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('dealId', '==', dealId),
    orderBy('createdAt', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as PresitePartsSelection
  );
}

/**
 * Get presite parts that haven't been added to playground yet
 */
export async function getPresitePartsNotInPlayground(
  dealId: string
): Promise<PresitePartsSelection[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('dealId', '==', dealId),
    where('addedToPlayground', '==', false),
    orderBy('createdAt', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as PresitePartsSelection
  );
}

/**
 * Get presite parts that haven't been converted to provisioning yet
 */
export async function getPresitePartsNotInProvisioning(
  dealId: string
): Promise<PresitePartsSelection[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('dealId', '==', dealId),
    where('addedToProvisioning', '==', false),
    orderBy('createdAt', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as PresitePartsSelection
  );
}

// ============================================================================
// UPDATE
// ============================================================================

/**
 * Update a presite part
 */
export async function updatePresitePart(
  partId: string,
  data: Partial<Omit<PresitePartsSelection, 'id' | 'createdAt' | 'dealId' | 'createdBy'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, partId);

  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Mark a presite part as added to playground
 */
export async function markPresitePartAddedToPlayground(
  partId: string
): Promise<void> {
  await updatePresitePart(partId, { addedToPlayground: true });
}

/**
 * Mark a presite part as added to provisioning
 */
export async function markPresitePartAddedToProvisioning(
  partId: string
): Promise<void> {
  await updatePresitePart(partId, { addedToProvisioning: true });
}

/**
 * Mark multiple presite parts as added to playground
 */
export async function markPresitePartsAddedToPlayground(
  partIds: string[]
): Promise<void> {
  const batch = writeBatch(db);

  partIds.forEach((partId) => {
    const docRef = doc(db, COLLECTION_NAME, partId);
    batch.update(docRef, {
      addedToPlayground: true,
      updatedAt: Timestamp.now(),
    });
  });

  await batch.commit();
  console.log(`[PRESITE PARTS] Marked ${partIds.length} parts as added to playground`);
}

/**
 * Mark multiple presite parts as added to provisioning
 */
export async function markPresitePartsAddedToProvisioning(
  partIds: string[]
): Promise<void> {
  const batch = writeBatch(db);

  partIds.forEach((partId) => {
    const docRef = doc(db, COLLECTION_NAME, partId);
    batch.update(docRef, {
      addedToProvisioning: true,
      updatedAt: Timestamp.now(),
    });
  });

  await batch.commit();
  console.log(
    `[PRESITE PARTS] Marked ${partIds.length} parts as added to provisioning`
  );
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Delete a presite part
 */
export async function deletePresitePart(partId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, partId);
  await deleteDoc(docRef);
}

/**
 * Delete all presite parts for a deal
 */
export async function deletePresitePartsByDeal(dealId: string): Promise<void> {
  const parts = await getPresitePartsByDeal(dealId);

  const batch = writeBatch(db);
  parts.forEach((part) => {
    if (part.id) {
      const docRef = doc(db, COLLECTION_NAME, part.id);
      batch.delete(docRef);
    }
  });

  await batch.commit();
  console.log(`[PRESITE PARTS] Deleted ${parts.length} parts for deal ${dealId}`);
}

// ============================================================================
// 🎯 BUSINESS LOGIC: CONVERT PRESITE PARTS TO PROVISIONING
// ============================================================================

/**
 * Convert presite parts to provisioning items
 *
 * This is called when a deal moves from QUOTE_REVIEW to WON
 * It creates provisioning_items from the presite parts selections
 *
 * @param dealId - The deal to convert parts for
 * @returns Array of created provisioning item IDs
 */
export async function convertPresitePartsToProvisioning(
  dealId: string
): Promise<string[]> {
  // Get all presite parts that haven't been converted yet
  const presiteParts = await getPresitePartsNotInProvisioning(dealId);

  if (presiteParts.length === 0) {
    console.warn(`No presite parts to convert for deal ${dealId}`);
    return [];
  }

  // Create provisioning items and track their IDs
  const provisioningItemIds: string[] = [];
  const presitePartIds: string[] = [];

  for (const presitePart of presiteParts) {
    // Create provisioning item
    const provisioningItemId = await createProvisioningItem({
      tenantId: getTenantIdSync(),
      dealId,
      itemName: presitePart.partName,
      quantity: presitePart.quantity || 1,
      status: ProvisioningItemStatus.PENDING,
      notes: presitePart.notes,
      metadata: {
        presitePartId: presitePart.id,
        sku: presitePart.sku,
        estimatedPrice: presitePart.estimatedPrice,
        isCustomPart: presitePart.isCustomPart,
        ...presitePart.metadata,
      },
    });

    provisioningItemIds.push(provisioningItemId);
    if (presitePart.id) {
      presitePartIds.push(presitePart.id);
    }
  }

  // Mark presite parts as converted
  if (presitePartIds.length > 0) {
    await markPresitePartsAddedToProvisioning(presitePartIds);
  }

  console.log(
    `[PRESITE → PROVISIONING] Converted ${presiteParts.length} presite parts to provisioning items for deal ${dealId}`
  );

  return provisioningItemIds;
}

/**
 * Get a summary of presite parts for a deal
 * Useful for displaying in the quote review UI
 */
export async function getPresitePartsSummary(dealId: string): Promise<{
  totalParts: number;
  totalQuantity: number;
  estimatedTotal: number;
  parts: PresitePartsSelection[];
}> {
  const parts = await getPresitePartsByDeal(dealId);

  const totalParts = parts.length;
  const totalQuantity = parts.reduce((sum, part) => sum + part.quantity, 0);
  const estimatedTotal = parts.reduce(
    (sum, part) => sum + (part.estimatedPrice || 0) * part.quantity,
    0
  );

  return {
    totalParts,
    totalQuantity,
    estimatedTotal,
    parts,
  };
}
